import { useDispatch } from 'react-redux';

// material-ui
import { Box, CardHeader, Checkbox, Dialog, Divider, FormLabel, Grid, InputLabel, Stack, TextField, Typography, Autocomplete } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// third-party
import _ from 'lodash';
import * as Yup from 'yup';
import { Form, FormikProvider, FormikValues, useFormik } from 'formik';

// project imports
import { openSnackbar } from 'store/reducers/snackbar';
// assets
import {
  Company,
  CompanyType,
  Rights,
  Roles,
  useIsEmailRegisteredLazyQuery,
  User,
  useSaveUserMutation,
  useSaveAndInviteUserMutation,
  useUsersQuery,
} from '../../graphql/types';
import statesTitlecase from '../../utils/states-titlecase';
import { PatternFormat } from 'react-number-format';
import useAuth from '../../hooks/useAuth';
import { roleList } from '../../utils/roles-list';
import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { CameraOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import yupSequence from '../../utils/yupSequence';
import Avatar from '../../components/@extended/Avatar';
import { stringAvatar } from '../../utils/avatar';
import { getUserProfileFullName } from '../../utils/users';
import { parseDateForPicker, formatDateForStorage, PICKER_DATE_FORMAT } from '../../utils/getFormattedDate';
import { useTheme } from '@mui/material/styles';
import ProfileAvatarEditor from '../profiles/account/ProfileAvatarEditor';
import fileToBase64 from '../../utils/fileToBase64';
import { AppConfig } from '../../config';
import { CompaniesAutocomplete } from '../companies/CompaniesAutocomplete';
import { Chip } from '@mui/material';
import IconButton from 'components/@extended/IconButton';

export const roleMap: Record<CompanyType, Roles> = {
  MERCHANT: Roles.Merchant,
  LEGAL: Roles.Legal,
  BROKER: Roles.Broker
};

interface Values {
  isLoginEnabled: boolean;
  isOwner: boolean;
  reportReturnedTransactions: boolean;
  name: string;
  email: string;
  role: Roles[];
  managerId: string | null;
  company: Company | null;
  data: {
    firstName: string;
    lastName: string | null;
    ssn: string;
    cellPhone: string;
    bizPhone: string;
    description: string;
    avatar: string;
    dateOfBirth: string | null;
    address: {
      line1: string;
      city: string;
      state: string;
      zip: string;
    };
  };
}

// constant
const maxDateBirth = new Date();
maxDateBirth.setFullYear(maxDateBirth.getFullYear() - 18);
const getRole = (type: string): Roles | undefined => roleMap[type as CompanyType];
const getInitialValues = (entity: FormikValues | null, company: Company | null, role: Roles[] | null, currentUser: User): Values => {
  const newEntity: Values = {
    isLoginEnabled: true,
    isOwner: false,
    reportReturnedTransactions: false,
    name: '',
    email: '',
    role: company?.type ? company.type.map(t => getRole(t)!) : role !== null ? role : [],
    managerId: '',
    company: company || null,
    data: {
      firstName: '',
      lastName: '',
      ssn: '',
      cellPhone: '',
      bizPhone: '',
      description: '',
      avatar: '',
      dateOfBirth: '1/1/1990',
      address: {
        line1: '',
        city: '',
        state: '',
        zip: ''
      }
    }
  };

  if (entity) {
    newEntity.isLoginEnabled = entity.isLoginEnabled;
    newEntity.isOwner = entity.isOwner || false;
    newEntity.reportReturnedTransactions = entity.enableReturnedTransactionsReport ?? false;
    newEntity.name = entity.name;
    newEntity.email = entity.email;
    newEntity.role = Array.isArray(entity.role) ? entity.role : [entity.role].filter(Boolean);
    newEntity.managerId = entity?.manager?.id;
    newEntity.company = entity?.company;
    newEntity.data = {
      firstName: entity.data.firstName || '',
      lastName: entity.data.lastName || '',
      ssn: entity.data.ssn || '',
      cellPhone: entity.data.cellPhone || '',
      bizPhone: entity.data.bizPhone || '',
      description: entity.data.description || '',
      avatar: entity.data.avatar || '',
      dateOfBirth: entity.data.dateOfBirth,
      address: {
        line1: entity.data?.address?.line1 || '',
        city: entity.data?.address?.city || '',
        state: entity.data?.address?.state || '',
        zip: entity.data?.address?.zip || ''
      }
    };
    const merged = _.merge({}, newEntity, entity);
    merged.role = newEntity.role;
    return merged;
  }

  if (!entity) {
    const isIsoManager = currentUser?.role?.includes(Roles.IsoManager);
    const isSuperAdmin = currentUser?.role?.includes(Roles.SuperAdmin);
    const isSubmissionAdmin = currentUser?.role?.includes(Roles.SubmissionAdmin);

    if (isIsoManager && !isSuperAdmin && !isSubmissionAdmin) {
      newEntity.role = [Roles.Broker];
      newEntity.managerId = currentUser.id;
    }
  }

  return newEntity;
};

// ==============================|| USER ADD / EDIT / DELETE ||============================== //

export interface Props {
  entity?: any;
  onCancel: () => void;
  onSave: (user: User) => void;
  company?: any;
  role?: Roles[];
  inviteOnSave?: boolean;
  onRolesChange?: (roles: Roles[]) => void;
  getPermissionsData?: () => { rights: Rights[] | null; hasCustomPermissions: boolean };
  onUserSaved?: (user: User) => Promise<void> | void;
}

const UserInfoTab = forwardRef(
  ({ entity, onCancel, onSave, company, role, inviteOnSave, onRolesChange, getPermissionsData, onUserSaved }: Props, ref) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user: currentUser } = useAuth();
  const canActivateLogin = currentUser?.rights.includes(Rights.ManageUsers);
  const [fetchIsEmailRegistered, { loading: fetchIsEmailRegisteredLoading }] = useIsEmailRegisteredLazyQuery({
    fetchPolicy: 'network-only'
  });
  const [saveUser] = useSaveUserMutation();
  const [saveAndInviteUser] = useSaveAndInviteUserMutation();
  const { data: managersData } = useUsersQuery({
    variables: { limit: 100, right: Rights.IsIsoManager, isActive: true },
    fetchPolicy: 'network-only'
  });
  const managers = useMemo(() => managersData?.users.data || [], [managersData]);
  const [currentValidValid, setCurrentValidValid] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [avatar, setAvatar] = useState<any | undefined>(undefined);
  const [adjustAvatar, setAdjustAvatar] = useState<boolean>(false);
  const [mediaFile, setMediaFile] = useState<File | undefined>();
  const handleCloseAvatarAdjustment = () => {
    setAdjustAvatar(!adjustAvatar);
    (document.getElementById('change-avtar') as HTMLInputElement).value = '';
  };
  useEffect(() => {
    setAvatar(entity?.data?.avatar ? AppConfig.apiRoot + 'files/avatar/' + entity?.data?.avatar : undefined);
  }, [entity]);

  useEffect(() => {
    const fetchData = async (file: File) => {
      setMediaFile(file);
      const base64 = await fileToBase64(file);
      setAvatar('data:image/png;base64,' + base64);
      setAdjustAvatar(true);
    };
    if (selectedImage) {
      fetchData(selectedImage).catch(console.error);
    }
  }, [selectedImage]);

  const UserSchema = Yup.object().shape({
    role: Yup.array().of(Yup.mixed<Roles>()).min(1, 'At least one role is required.').required('Role is required.'),
    managerId: Yup.string()
      .nullable()
      .when('role', {
        is: (roles: Roles[]) => roles?.includes(Roles.Broker),
        then: () => Yup.string().nullable().required('ISO Manager is required')
      }),
    company: Yup.object()
      .nullable()
      .when('role', {
        is: (roles: Roles[]) => roles?.some(role => [Roles.Legal, Roles.Merchant, Roles.Broker].includes(role)),
        then: () => Yup.object().required('Assigning Company is required')
      }),
    email: Yup.string().test(
      yupSequence(
        'email',
        Yup.string().email('Invalid email address.').max(255).required('Email is required.'),
        Yup.string().test('Unique Email', 'Email already in use', async function (value) {
          if ((entity && entity.id) || (currentValidValid && value == currentValidValid)) {
            return true;
          }
          const res = await fetchIsEmailRegistered({ variables: { email: value || '' } });
          const isEmailRegistered = res.data?.isEmailRegistered;
          if (!isEmailRegistered) {
            setCurrentValidValid(value || '');
          }
          return !isEmailRegistered;
        })
      )
    ),
    isOwner: Yup.boolean(),
    reportReturnedTransactions: Yup.boolean(),
    data: Yup.object().shape({
      description: Yup.string().nullable().max(500),
      bizPhone: Yup.string()
        .nullable()
        .test('phone-validation', 'Phone is required', function (value) {
          if (!value || value.replace(/\D/g, '').length === 0) return this.createError({ message: 'Phone is required' });
          if (value.replace(/\D/g, '').length !== 11) return this.createError({ message: 'Phone must be 10 digits USA number' });
          return true;
        }),
      firstName: Yup.string().max(255).required('First Name is required'),
      address: Yup.object().shape({
        zip: Yup.string()
          .trim()
          .matches(/^\d{5}(-\d{4})?$/, {
            message: 'ZIP must be 5 or 5-4 digits (e.g. 12345 or 12345-6789)',
            excludeEmptyString: true
          })
      })
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(entity!, company, role || null, currentUser as User),
    validationSchema: UserSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const company = values?.company;
        const mutation = inviteOnSave ? saveAndInviteUser : saveUser;
        const permissionsData = getPermissionsData ? getPermissionsData() : { rights: [], hasCustomPermissions: false };
        const saved = await mutation({
          variables: {
            dto: {
              id: entity ? entity.id : null,
              name: entity?.name ? entity.name : values.data.firstName,
              isLoginEnabled: canActivateLogin ? values.isLoginEnabled : false,
              isOwner: values.isOwner,
              enableReturnedTransactionsReport: values.reportReturnedTransactions,
              email: values.email,
              role: values.role,
              managerId: values.managerId,
              companyId: company ? (company as Company).id : null,
              rights: permissionsData.rights || [],
              data: {
                firstName: values.data.firstName,
                lastName: values.data.lastName,
                ssn: values.data.ssn,
                description: values.data.description,
                dateOfBirth: values.data.dateOfBirth,
                bizPhone: values.data.bizPhone,
                avatar: values.data.avatar,
                address: {
                  line1: values.data?.address?.line1 || '',
                  city: values.data?.address?.city || '',
                  state: values.data?.address?.state || '',
                  zip: values.data?.address?.zip || ''
                }
              },
              avatarMediaFile: mediaFile ? ((await fileToBase64(mediaFile)) as string) : null,
            }
          }
        });

        const savedUser = saved.data && ('saveUser' in saved.data ? saved.data.saveUser : saved.data.saveAndInviteUser);

        if (!saved.data || !savedUser) {
          dispatch(
            openSnackbar({
              open: true,
              message: 'Operation failed, please try again later.',
              variant: 'alert',
              alert: {
                color: 'error'
              },
              close: true
            })
          );
          setSubmitting(false);
          return;
        }
        if (onUserSaved && savedUser?.id) {
          try {
            await onUserSaved(savedUser as User);
          } catch (e) {
            console.error(e);
          }
        }
        onSave(savedUser as User);
        if (entity) {
          dispatch(
            openSnackbar({
              open: true,
              message: 'User update successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        } else {
          dispatch(
            openSnackbar({
              open: true,
              message: inviteOnSave ? 'User added and invited successfully.' : 'User added successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        }

        setSubmitting(false);
      } catch (error) {
        console.error(error);
      }
    }
  });
  useImperativeHandle(ref, () => ({
    submitForm: () => formik.submitForm(),
    isEmailChecking: () => fetchIsEmailRegisteredLoading
  }));
  const handleDelete = () => {
    setSelectedImage(undefined);
    setAvatar(null);
    setMediaFile(undefined);
    formik.setFieldValue('data.avatar', null);
  };
  const { errors, touched, handleSubmit, getFieldProps, values, setFieldValue, handleBlur, handleChange, submitCount } = formik;

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={3}>
                <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start" width="100%">
                      {/* Avatar Section */}
                      <Stack spacing={1.5} alignItems="center" sx={{ ml: 3, mt: 0.5, position: 'relative' }}>
                        <FormLabel
                          htmlFor="change-avtar"
                          sx={{
                            position: 'relative',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            '&:hover .MuiBox-root': { opacity: 1 },
                            cursor: 'pointer'
                          }}
                        >
                          {avatar ? (
                            <Avatar alt="User profile" src={avatar} sx={{ width: 150, height: 150, border: '1px dashed' }} />
                          ) : (
                            <Avatar
                              alt="User profile"
                              {...stringAvatar(getUserProfileFullName(entity))}
                              sx={{ width: 150, height: 150, border: '1px dashed' }}
                            />
                          )}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .75)' : 'rgba(0,0,0,.65)',
                              width: '100%',
                              height: '100%',
                              opacity: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Stack spacing={0.5} alignItems="center">
                              <CameraOutlined style={{ color: theme.palette.secondary.lighter, fontSize: '2rem' }} />
                              <Typography sx={{ color: 'secondary.lighter' }}>Upload</Typography>
                            </Stack>
                          </Box>
                        </FormLabel>
                        <TextField
                          variant="filled"
                          type="file"
                          id="change-avtar"
                          label="Outlined"
                          sx={{ display: 'none' }}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedImage(e.target.files?.[0])}
                        />
                        <IconButton
                          aria-label="delete avatar"
                          color="error"
                          sx={{
                            position: 'absolute',
                            top: '103px',
                            left: '138px',
                            p: 0
                          }}
                          onClick={handleDelete}
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </Stack>

                      {/* Form Fields */}
                      <Stack spacing={2} flex={1}>
                        <Stack spacing={1.25}>
                          <InputLabel required={true} htmlFor="entity-name">
                            First Name / Last Name
                          </InputLabel>
                          <TextField
                            variant="filled"
                            fullWidth
                            id="entity-name"
                            placeholder="Enter User First Name"
                            {...getFieldProps('data.firstName')}
                            error={Boolean(touched.data?.firstName && errors.data?.firstName)}
                            helperText={touched.data?.firstName && errors.data?.firstName}
                          />
                        </Stack>

                        <Stack spacing={1.25}>
                          <TextField
                            variant="filled"
                            fullWidth
                            id="entity-lastName"
                            placeholder="Enter User Last Name"
                            {...getFieldProps('data.lastName')}
                            error={Boolean(touched.data?.lastName && errors.data?.lastName)}
                            helperText={touched.data?.lastName && errors.data?.lastName}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel required={true} htmlFor="entity-email">
                      Email Address
                    </InputLabel>
                    <TextField
                      variant="filled"
                      disabled={!!entity}
                      fullWidth
                      id="entity-email"
                      placeholder="Enter User Email Address"
                      {...getFieldProps('email')}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel required={true} htmlFor="entity-phone">
                      Phone
                    </InputLabel>
                    <PatternFormat
                      id="entity-phone"
                      variant="filled"
                      format="+1-###-###-####"
                      mask="_"
                      fullWidth
                      placeholder="Enter User Phone"
                      customInput={TextField}
                      {...getFieldProps('data.bizPhone')}
                      error={Boolean(touched.data?.bizPhone && errors.data?.bizPhone)}
                      helperText={touched.data?.bizPhone && errors.data?.bizPhone}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel required={true} htmlFor="entity-role">
                      Role
                    </InputLabel>
                    <Autocomplete
                      multiple
                      id="companyTypes"
                      options={roleList(currentUser?.rights || [])}
                      getOptionLabel={(option) => option.name}
                      value={roleList(currentUser?.rights || []).filter((role) => (Array.isArray(values.role) ? values.role : []).includes(role.key)).sort((a, b) => (Array.isArray(values.role) ? values.role : []).indexOf(a.key) - (Array.isArray(values.role) ? values.role : []).indexOf(b.key))}
                      onChange={(event, newValue) => {
                        const newRoles = newValue.map((role) => role.key);
                        setFieldValue('role', newRoles);
                        if (onRolesChange) {
                          onRolesChange(newRoles);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched('role', true)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="combined" label={option.name} {...getTagProps({ index })} key={option.key} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="filled"
                          fullWidth
                          placeholder="Select Roles"
                          error={Boolean(errors.role && (touched.role || submitCount > 0))}
                          helperText={(touched.role || submitCount > 0) && errors.role}
                          InputProps={{
                            ...params.InputProps,
                            sx: {
                              py: 1,
                              alignItems: 'flex-start',
                              flexWrap: 'wrap',
                              gap: '4px',
                              '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
                                fontSize: '12px'
                              },
                              '& .MuiChip-root': {
                                height: 30,
                                fontSize: '12px',
                                margin: '0 2px'
                              },
                              '& .MuiChip-deleteIcon, & .MuiAutocomplete-clearIndicator svg': {
                                fontSize: 17,
                                width: 17,
                                height: 17
                              }
                            },
                          }}

                        />
                      )}
                    />
                  </Stack>
                </Grid>
                {(() => {
                  const companyRoles = [Roles.Merchant, Roles.Legal, Roles.Broker, Roles.Collections];
                  const selectedCompanyRoles = (Array.isArray(values.role) ? values.role : []).filter(role => companyRoles.includes(role));
                  const companyTypes: CompanyType[] = selectedCompanyRoles.map(role => {
                    switch (role) {
                      case Roles.Merchant: return CompanyType.Merchant;
                      case Roles.Legal: return CompanyType.Legal;
                      case Roles.Broker: return CompanyType.Broker;
                      case Roles.Collections: return CompanyType.Legal;
                      default: return CompanyType.Merchant;
                    }
                  }).filter((type, index, arr) => arr.indexOf(type) === index);

                  const hasBroker = (Array.isArray(values.role) ? values.role : []).includes(Roles.Broker);

                  if (selectedCompanyRoles.length === 0) return null;


                  return (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1.25}>
                          <InputLabel required={true} htmlFor="user-entity-company">
                            Company
                          </InputLabel>
                          <CompaniesAutocomplete
                            id={'user-entity-company'}
                            disabled={!!company}
                            companyTypes={companyTypes}
                          />
                          {touched.company && errors.company && (
                            <p className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained css-62fqw2-MuiFormHelperText-root">
                              {errors.company}
                            </p>
                          )}
                        </Stack>
                      </Grid>
                      {hasBroker && (
                        <Grid item xs={12} sm={6}>
                          <Stack spacing={1.25}>
                            <InputLabel required={true} htmlFor="user-entity-manager">
                              ISO Manager
                            </InputLabel>
                            <TextField
                              variant="filled"
                              id="user-entity-manager"
                              error={Boolean(touched.managerId && errors.managerId)}
                              helperText={touched.managerId && errors.managerId}
                              fullWidth
                              name="managerId"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.managerId || ''}
                              select
                              SelectProps={{ native: true }}
                            >
                              <option value={''}>Select ISO Manager</option>
                              <option key={currentUser?.id} value={currentUser?.id}>
                                {getUserProfileFullName(currentUser)} ({[...(currentUser?.role || [])].join(', ')})
                              </option>
                              {managers
                                .filter((manager) => manager.id !== currentUser?.id)
                                .filter((manager, index, self) => self.findIndex((m) => m.id === manager.id) === index)
                                .map((manager) => (
                                  <option key={manager.id} value={manager.id}>
                                    {getUserProfileFullName(manager)} ({(manager.role || []).join(', ')})
                                  </option>
                                ))}
                            </TextField>
                          </Stack>
                        </Grid>
                      )}
                    </>
                  );
                })()}
              </Grid>
            </Box>
            <CardHeader title="Additional Details" />
            <Divider />
            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel htmlFor="entity-data-address-line1">Address</InputLabel>
                    <TextField
                      variant="filled"
                      fullWidth
                      id="entity-data-address-line1"
                      placeholder="Enter User Address"
                      {...getFieldProps('data.address.line1')}
                      error={Boolean(touched.data?.address?.line1 && errors.data?.address?.line1)}
                      helperText={touched.data?.address?.line1 && errors.data?.address?.line1}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel htmlFor="entity-data-address-city">City</InputLabel>
                    <TextField
                      variant="filled"
                      fullWidth
                      id="entity-data-address-city"
                      placeholder="Enter User Address City"
                      {...getFieldProps('data.address.city')}
                      error={Boolean(touched.data?.address?.city && errors.data?.address?.city)}
                      helperText={touched.data?.address?.city && errors.data?.address?.city}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel htmlFor="entity-data-address-state">State</InputLabel>
                    <TextField
                      variant="filled"
                      error={Boolean(touched.data?.address?.state && errors.data?.address?.state)}
                      helperText={touched.data?.address?.state && errors.data?.address?.state}
                      fullWidth
                      id="entity-data-address-state"
                      name="data.address.state"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.data?.address?.state}
                      select
                      SelectProps={{ native: true }}
                    >
                      <option value={''}>Select State</option>
                      {statesTitlecase.map(({ name, abbreviation }) => (
                        <option key={abbreviation} value={name}>
                          {name}
                        </option>
                      ))}
                    </TextField>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel htmlFor="entity-data-address-state">Zip</InputLabel>
                    <TextField
                      variant="filled"
                      fullWidth
                      id="entity-data-address-zip"
                      placeholder="Enter User Address Zip"
                      {...getFieldProps('data.address.zip')}
                      error={Boolean(touched.data?.address?.zip && errors.data?.address?.zip)}
                      helperText={touched.data?.address?.zip && errors.data?.address?.zip}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel htmlFor="entity-data-ssn">SSN</InputLabel>
                    <TextField
                      variant="filled"
                      fullWidth
                      id="entity-data-ssn"
                      placeholder="Enter User SSN"
                      {...getFieldProps('data.ssn')}
                      error={Boolean(touched.data?.ssn && errors.data?.ssn)}
                      helperText={touched.data?.ssn && errors.data?.ssn}
                    />
                    {canActivateLogin && (
                      <InputLabel htmlFor="entity-is-isLoginEnabled" sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                        <Checkbox id="entity-is-isLoginEnabled" {...getFieldProps('isLoginEnabled')} checked={values.isLoginEnabled} />
                        User login active
                      </InputLabel>
                    )}
                    <InputLabel htmlFor="entity-is-owner" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                      <Checkbox id="entity-is-owner" {...getFieldProps('isOwner')} checked={values.isOwner} />
                      User is owner
                    </InputLabel>
                    <InputLabel htmlFor="entity-report-returned-transactions" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                      <Checkbox id="entity-report-returned-transactions" {...getFieldProps('reportReturnedTransactions')} checked={values.reportReturnedTransactions} />
                      Report returned transactions
                    </InputLabel>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.25}>
                    <InputLabel htmlFor="entity-dateOfBirth">Date of Birth</InputLabel>
                    <DesktopDatePicker
                      value={parseDateForPicker(values.data.dateOfBirth)}
                      maxDate={maxDateBirth}
                      format={PICKER_DATE_FORMAT}
                      onChange={(value) => setFieldValue('data.dateOfBirth', formatDateForStorage(value))}
                      slotProps={{
                        textField: {
                          variant: 'filled',
                          error: Boolean(touched?.data?.dateOfBirth && errors?.data?.dateOfBirth),
                          name: 'data.dateOfBirth',
                          onBlur: handleBlur
                        }
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <InputLabel htmlFor="entity-data-description" sx={{ mb: 1 }}>Note</InputLabel>
              <TextField
                variant="filled"
                fullWidth
                id="entity-data-description"
                multiline
                rows={5}
                placeholder="Enter User Notes"
                {...getFieldProps('data.description')}
                error={Boolean(touched.data?.description && errors.data?.description)}
                helperText={touched.data?.description && errors.data?.description}
              />
            </Box>
          </Form>
        </LocalizationProvider >
      </FormikProvider >
      <Dialog maxWidth="sm" fullWidth onClose={handleCloseAvatarAdjustment} open={adjustAvatar} sx={{ '& .MuiDialog-paper': { p: 0 } }}>

        {adjustAvatar && (
          <ProfileAvatarEditor
            sourceImage={avatar!}
            onSave={(image) => {
              setAvatar(image.toDataURL());
              image.toBlob((blob: Blob) => {
                setMediaFile(blob as File);
              });
              handleCloseAvatarAdjustment();
            }}
            onClose={handleCloseAvatarAdjustment}
            onCancel={() => {
              setAvatar(entity?.data?.avatar || undefined);
              handleCloseAvatarAdjustment();
            }}
          />
        )}
      </Dialog >
    </>
  );
});

export default UserInfoTab;